import { fetchConfig } from '@theme/utilities';

/*
  «Richiedi fattura» nel carrello: una riga apre la finestra con i dati aziendali;
  «Salva informazioni aziendali» li scrive come attributi del carrello (arrivano
  sull'ordine) e li ricorda su questo dispositivo per il prossimo acquisto.
  Il riepilogo del carrello viene ridisegnato a ogni modifica: l'elemento si
  inizializza da solo ogni volta che viene inserito nella pagina.
*/

const FLAG = 'Fattura richiesta';
const FIELDS = {
  company: 'Ragione sociale',
  vat: 'Partita IVA',
  tax_code: 'Codice fiscale',
  sdi: 'Codice SDI',
  pec: 'PEC',
  address: 'Indirizzo',
  zip: 'CAP',
  city: 'Città',
  province: 'Provincia',
};
const STORAGE_KEY = 'tanca:invoice';

/** Partita IVA italiana: 11 cifre con cifra di controllo. */
function isValidVat(value) {
  if (!/^\d{11}$/.test(value)) return false;
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let digit = Number(value[i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

const CHECKS = {
  company: (v) => (v ? '' : 'Inserisci la ragione sociale.'),
  vat: (v) => (isValidVat(v) ? '' : 'Inserisci una partita IVA valida (11 cifre).'),
  tax_code: (v) => (!v || /^([A-Z0-9]{16}|\d{11})$/.test(v) ? '' : 'Il codice fiscale ha 16 caratteri (11 cifre per le società).'),
  sdi: (v) => (!v || /^[A-Z0-9]{6,7}$/.test(v) ? '' : 'Il codice SDI ha 7 caratteri (6 per la pubblica amministrazione).'),
  pec: (v) => (!v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Inserisci un indirizzo PEC valido.'),
  address: (v) => (v ? '' : "Inserisci l'indirizzo della sede."),
  zip: (v) => (/^\d{5}$/.test(v) ? '' : 'Il CAP ha 5 cifre.'),
  city: (v) => (v ? '' : 'Inserisci la città.'),
  province: (v) => (/^[A-Z]{2}$/.test(v) ? '' : 'Inserisci la sigla della provincia (es. MI).'),
};

function readStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function writeStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Archivio del browser non disponibile: i dati restano comunque sull'ordine.
  }
}

class TancaInvoiceRequest extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.trigger = this.querySelector('[data-invoice-open]');
    if (!this.dialog || !this.form || !this.trigger) return;

    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.trigger.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-invoice-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-invoice-remove]')?.addEventListener('click', () => this.remove(), { signal });
    this.form.addEventListener('submit', (event) => this.save(event), { signal });
    this.form.addEventListener('input', (event) => this.clearError(event.target), { signal });
    // Clic sullo sfondo: chiude solo la finestra, non il carrello.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    }, { signal });
    // Esc chiude la finestra senza far chiudere anche il carrello che la contiene.
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  field(name) {
    return this.form.elements.namedItem(name);
  }

  open() {
    if (this.dataset.requested !== 'true') this.prefillFromStorage();
    this.dialog.showModal();
    const firstEmpty = Object.keys(FIELDS).map((name) => this.field(name)).find((input) => input && !input.value);
    (firstEmpty || this.field('company'))?.focus();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
    this.trigger.focus();
  }

  prefillFromStorage() {
    const saved = readStorage();
    if (!saved) return;
    for (const name of Object.keys(FIELDS)) {
      const input = this.field(name);
      if (input && !input.value && saved[name]) input.value = saved[name];
    }
  }

  values() {
    const data = {};
    for (const name of Object.keys(FIELDS)) {
      let value = (this.field(name)?.value || '').trim();
      if (['vat', 'tax_code', 'sdi', 'province'].includes(name)) value = value.toUpperCase().replace(/\s+/g, '');
      if (name === 'vat') value = value.replace(/^IT/, '');
      if (name === 'pec') value = value.toLowerCase();
      data[name] = value;
    }
    return data;
  }

  setError(name, message) {
    const input = this.field(name);
    const error = this.querySelector(`[data-error-for="${name}"]`);
    if (!input || !error) return;
    input.toggleAttribute('aria-invalid', Boolean(message));
    error.textContent = message;
    error.hidden = !message;
  }

  clearError(target) {
    if (target?.name) this.setError(target.name, '');
    if (target?.name === 'sdi' || target?.name === 'pec') this.setError('sdi', '');
    this.showStatus('');
  }

  showStatus(message) {
    const status = this.querySelector('[data-invoice-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  }

  validate(data) {
    let firstInvalid = null;
    for (const [name, check] of Object.entries(CHECKS)) {
      const message = check(data[name]);
      this.setError(name, message);
      if (message && !firstInvalid) firstInvalid = name;
    }
    if (!data.sdi && !data.pec) {
      this.setError('sdi', 'Inserisci il codice SDI oppure la PEC.');
      firstInvalid ||= 'sdi';
    }
    if (firstInvalid) this.field(firstInvalid)?.focus();
    return !firstInvalid;
  }

  async update(attributes) {
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes }) }));
    if (!response.ok) throw new Error(`cart update ${response.status}`);
  }

  async save(event) {
    event.preventDefault();
    const data = this.values();
    if (!this.validate(data)) return;

    const attributes = { [FLAG]: 'Sì' };
    for (const [name, label] of Object.entries(FIELDS)) attributes[label] = data[name];

    const button = this.form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    try {
      await this.update(attributes);
      writeStorage(data);
      this.dataset.requested = 'true';
      this.querySelector('[data-invoice-summary]').textContent = `${data.company} · P.IVA ${data.vat}`;
      this.querySelector('[data-invoice-remove]').hidden = false;
      this.close();
    } catch {
      this.showStatus('Non è stato possibile salvare i dati. Riprova tra qualche istante.');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  async remove() {
    const attributes = { [FLAG]: '' };
    for (const label of Object.values(FIELDS)) attributes[label] = '';
    try {
      await this.update(attributes);
      this.dataset.requested = 'false';
      this.querySelector('[data-invoice-summary]').textContent = this.dataset.emptyLabel || '';
      this.querySelector('[data-invoice-remove]').hidden = true;
      this.close();
    } catch {
      this.showStatus('Non è stato possibile aggiornare il carrello. Riprova tra qualche istante.');
    }
  }
}

if (!customElements.get('tanca-invoice-request')) {
  customElements.define('tanca-invoice-request', TancaInvoiceRequest);
}
